import { ViewModelFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import { CalendarState, TFunction } from '../../controller';
import { CalendarContext } from '../../../../utils/context/contextFactory';
import { formatRfcTimeStringToTimeSlotView } from '../../../../utils/dateAndTime/dateAndTime';
import { TimeSlotAvailabilityStatus } from '../../../../utils/timeSlots/timeSlots';
import settingsParams from '../../settingsParams';
import { MemoizedViewModalFactory } from '../viewModel';

export interface TimeSlotStatus extends TimeSlotAvailabilityStatus {
  selected: boolean;
}

export interface TimeSlot {
  formattedStartTime: string;
  rfcStartTime: string;
  status: TimeSlotStatus;
  ariaLabel: string;
}

export type TimeSelectionViewModel = {
  timeSlots: TimeSlot[];
  shouldLimitNumberOfTimeSlotsDisplayed: boolean;
  maxNumberOfTimeSlotsToDisplay: number;
  showAllButtonText: string;
  waitlistText: string;
};

type TimeSelectionViewModelParams = ViewModelFactoryParams<
  CalendarState,
  CalendarContext
> & {
  timeSlotsAvailabilityStatuses: Map<string, TimeSlotAvailabilityStatus>;
};

export const memoizedTimeSelectionViewModel: MemoizedViewModalFactory<TimeSelectionViewModel> = {
  dependencies: {
    state: ['selectedTime'],
    settings: [
      'waitlistIndication',
      'limitTimeSlotsDisplay',
      'maxTimeSlotsDisplayedPerDay',
      'loadMoreTimeSlots',
    ],
  },
};

export function createTimeSelectionViewModel({
  timeSlotsAvailabilityStatuses,
  state,
  context,
}: TimeSelectionViewModelParams): TimeSelectionViewModel {
  const { selectedTime } = state;
  const { businessInfo, t, settings, getContent } = context;
  const locale = businessInfo!.dateRegionalSettingsLocale;

  const waitlistText = getContent({
    settingsParam: settingsParams.waitlistIndication,
    translationKey: 'app.settings.defaults.waitlist',
  });
  const timeSlots: TimeSlot[] = [];
  timeSlotsAvailabilityStatuses.forEach(
    (timeSlotStatus: TimeSlotAvailabilityStatus, rfcStartTime: string) => {
      const formattedStartTime = rfcStartTime
        ? formatRfcTimeStringToTimeSlotView(rfcStartTime, locale)
        : '';
      const isTimeSelected = rfcStartTime === selectedTime;
      const ariaLabel = getTimeSlotAriaLabel(
        timeSlotStatus,
        formattedStartTime,
        t,
        waitlistText,
      );

      timeSlots.push({
        rfcStartTime,
        formattedStartTime,
        status: { ...timeSlotStatus, selected: isTimeSelected },
        ariaLabel,
      });
    },
  );

  return {
    timeSlots,
    shouldLimitNumberOfTimeSlotsDisplayed: settings.get(
      settingsParams.limitTimeSlotsDisplay,
    ),
    maxNumberOfTimeSlotsToDisplay: settings.get(
      settingsParams.maxTimeSlotsDisplayedPerDay,
    ),
    showAllButtonText: getContent({
      settingsParam: settingsParams.loadMoreTimeSlots,
      translationKey: 'app.settings.defaults.time-picker.show-all',
    }),
    waitlistText,
  };
}

const getTimeSlotAriaLabel = (
  timeSlotStatus: TimeSlotAvailabilityStatus,
  formattedStartTime: string,
  t: TFunction,
  waitlistText: string,
): string => {
  if (timeSlotStatus.withWaitingList) {
    return t('app.time-picker.accessibility.time-slot-with-waitlist', {
      time: formattedStartTime,
      waitlist: waitlistText,
    });
  }
  if (timeSlotStatus.tooLateToBookAllSlots || timeSlotStatus.allSlotsAreFull) {
    return t(
      'app.time-picker.accessibility.time-slot-closed-for-registration',
      { time: formattedStartTime },
    );
  }
  if (timeSlotStatus.tooEarlyToBookAllSlots) {
    return t(
      'app.time-picker.accessibility.time-slot-not-open-yet-for-registration',
      { time: formattedStartTime },
    );
  }
  return formattedStartTime;
};
